import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmationModal } from "../../../components/organisms/Modal/confirmationModal";
import { useNotification } from "../../../hooks/useNotification";
import {
  useCancelSalesOrderMutation,
  usePatchSalesOrderMutation,
} from "../../../store/apiSlice/salesApiSlice";
import { componentWithCondition } from "../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ListActionBurgerMenu } from "../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";
import { FulfilWizard } from "../detail/FulfilWizard";
import { fulfilmentSelectionState } from "./fulfilmentSelectionHelpers";

export const SalesOrderAction = ({
  selectedOrderId,
  setSelectedOrder,
  selectedOrderFromListview,
}) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [cancelOrder, { isLoading: isCancelLoading }] = useCancelSalesOrderMutation();
  const [patchOrder, { isLoading: isConfirmLoading }] = usePatchSalesOrderMutation();

  const [isCancelling, setIsCancelling] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [fulfilOpen, setFulfilOpen] = useState(false);

  const fulfilContext = useMemo(
    () => fulfilmentSelectionState(selectedOrderFromListview),
    [selectedOrderFromListview],
  );

  const handleCreateRedirect = () => {
    navigate("/sales/order/create");
  };

  const handleCancelSubmit = async () => {
    try {
      await cancelOrder({ id: selectedOrderId[0], reason: cancellationReason.trim() }).unwrap();
      notify({
        type: "success",
        message: `Sales Order “${selectedOrderFromListview[0]?.name}” cancelled successfully.`,
      });
      setSelectedOrder([]);
      setIsCancelling(false);
      setCancellationReason("");
    } catch (error) {
      notify({ type: "error", message: error.data?.detail || "Failed to cancel sales order." });
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      await patchOrder({ id: selectedOrderId[0], status_id: "created" }).unwrap();
      notify({
        type: "success",
        message: `Sales Order “${selectedOrderFromListview[0]?.name}” confirmed successfully.`,
      });
      setSelectedOrder([]);
      setIsConfirming(false);
    } catch (error) {
      notify({ type: "error", message: error.data?.detail || "Failed to confirm sales order." });
    }
  };

  const isDraftSelected =
    selectedOrderFromListview[0]?.status?.id === "draft" ||
    selectedOrderFromListview[0]?.status === "draft";
  const selectedOrder = selectedOrderFromListview[0];
  const selectedStatus = selectedOrder?.status?.id || selectedOrder?.status;
  const cancellableStatuses = ["draft", "created", "partially_allocated", "fully_allocated"];
  const canCancelSelected =
    selectedOrderId.length === 1 &&
    (typeof selectedOrder?.can_cancel === "boolean"
      ? selectedOrder.can_cancel
      : cancellableStatuses.includes(selectedStatus));
  const cancelBlockers = selectedOrder?.cancel_blockers || [];

  const handleCancelClick = () => {
    if (!canCancelSelected) {
      notify({
        type: "error",
        message:
          cancelBlockers.join(" ") ||
          `Sales orders in ${selectedOrder?.status?.value || selectedStatus || "this"} status cannot be cancelled.`,
      });
      return;
    }
    setIsCancelling(true);
  };

  const isReturnActive = useMemo(() => {
    if (selectedOrderId.length !== 1) return false;
    const statusId = selectedOrderFromListview[0]?.status?.id;
    return statusId === "fully_delivered" || statusId === "dispatched";
  }, [selectedOrderId, selectedOrderFromListview]);

  const canCreateInvoice =
    selectedOrderId.length === 1 && selectedStatus !== "cancelled" && selectedStatus !== "draft";

  const handleCreateInvoice = () => {
    if (!canCreateInvoice) return;
    navigate("/sale_invoice/create", {
      state: {
        paramId: selectedOrder.id,
        customer: selectedOrder.customer,
        currency_id: selectedOrder.currency?.id || selectedOrder.currency_id,
      },
    });
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "create-invoice",
        label: "Create Invoice",
        onClick: handleCreateInvoice,
        disabled: !canCreateInvoice,
        title: canCreateInvoice ? "" : "Select a confirmed sales order to create an invoice.",
      },
      {
        key: "fulfilment",
        label: "Fulfilment",
        onClick: () => setFulfilOpen(true),
        disabled: !fulfilContext.ok,
        title: fulfilContext.reason || "",
      },
      {
        key: "return",
        label: "Return",
        onClick: () => {
          navigate("/customer_return/create", {
            state: { salesOrder: selectedOrderFromListview[0] },
          });
        },
        disabled: !isReturnActive,
        title: isReturnActive ? "" : "Select a dispatched or fully delivered sales order.",
      },
      {
        key: "cancel",
        label: "Cancel",
        onClick: handleCancelClick,
        disabled: selectedOrderId.length !== 1 || isCancelLoading,
        title:
          cancelBlockers.join(" ") ||
          (selectedOrderId.length !== 1 ? "Select a single order to cancel." : ""),
        isLoading: isCancelLoading,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fulfilContext,
      canCreateInvoice,
      isReturnActive,
      selectedOrderId.length,
      cancelBlockers,
      selectedOrderFromListview,
      isCancelLoading,
    ],
  );

  return (
    <>
      <li className={`tableButton activeState`}>
        <button onClick={handleCreateRedirect}>Create</button>
      </li>
      <li
        className={`tableButton ${selectedOrderId.length === 1 && isDraftSelected ? "activeState" : "disableState"}`}
      >
        <button
          onClick={() => setIsConfirming(true)}
          disabled={selectedOrderId.length !== 1 || !isDraftSelected}
          title={
            selectedOrderId.length === 1 && isDraftSelected
              ? ""
              : "Select a single draft sales order to confirm."
          }
        >
          Confirm
        </button>
      </li>
      <ListActionBurgerMenu items={burgerItems} />

      {isCancelling && (
        <ConfirmationModal
          titleOfModal="Cancel Sales Order"
          btnName="Cancel Order"
          closeModal={() => {
            setIsCancelling(false);
            setCancellationReason("");
          }}
          handleModalWork={handleCancelSubmit}
          isLoading={isCancelLoading}
          description={
            <>
              Are you sure you want to cancel the sales order <strong>{selectedOrder?.name}</strong>
              ?
              <label className="d-block mt-4" htmlFor="sales-order-cancellation-reason">
                Cancellation reason (optional)
              </label>
              <textarea
                id="sales-order-cancellation-reason"
                className="form-control mt-2"
                value={cancellationReason}
                onChange={event => setCancellationReason(event.target.value)}
                placeholder="Enter a reason for the audit history"
                rows={3}
              />
            </>
          }
        />
      )}

      {fulfilOpen && fulfilContext.ok ? (
        <FulfilWizard
          orderIds={selectedOrderId}
          open={fulfilOpen}
          onClose={() => setFulfilOpen(false)}
        />
      ) : null}

      {isConfirming && (
        <ConfirmationModal
          titleOfModal="Confirm Sales Order"
          btnName="Confirm Order"
          closeModal={() => {
            setIsConfirming(false);
          }}
          handleModalWork={handleConfirmSubmit}
          isLoading={isConfirmLoading}
          description={
            <>
              Are you sure you want to confirm the sales order{" "}
              <strong>{selectedOrderFromListview[0]?.name}</strong>?
            </>
          }
        />
      )}
    </>
  );
};

export const SalesOrderActionWithCheck = componentWithCondition(SalesOrderAction);
